import React from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { formatSimpleDate } from 'shared-utils/utils/formatDate';
import { createTrackingProps } from 'shared-utils/analytics';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import type {
  BenefitsVerificationResponse,
  BenefitsVerificationDetails,
  BenefitsPreferenceDetails,
  CostShare,
} from '../../api';
import { SITE_OF_CARE_TYPE_LABELS } from '../../api';
import type { SiteOfCareType } from '../../api';

const NETWORK_STATUS_LABELS: Record<string, string> = {
  INN: 'In-network',
  OON: 'Out-of-network',
  UNKNOWN: 'Unknown',
};

const formatCopay = (copay: { amount: number; raw_text: string | null }): string => {
  const base = `$${copay.amount}`;
  return copay.raw_text ? `${base} (${copay.raw_text})` : base;
};

const formatAccumulator = (accumulator: { limit: number; remaining: number | null }): string => {
  const base = `$${accumulator.limit} total`;
  if (accumulator.remaining != null) {
    return `${base}, $${accumulator.remaining} remaining`;
  }
  return base;
};

const formatCoinsurance = (coinsurance: { percentage: number; raw_text: string | null }): string => {
  const base = `${coinsurance.percentage}% co-insurance`;
  return coinsurance.raw_text ? `${base} ${coinsurance.raw_text}` : base;
};

type SectionRow = {
  label: string;
  value: string | null;
};

type Section = {
  title: string;
  rows: SectionRow[];
};

const BenefitsVerificationTable = ({ sections }: { sections: Section[] }) => {
  return (
    <div className="overflow-hidden rounded border border-solid border-grayscale-100">
      {sections.map((section, sectionIdx) => (
        <div
          key={section.title}
          className={`flex ${sectionIdx < sections.length - 1 ? 'border-grayscale-100 border-b-solid' : ''}`}
        >
          <div
            className="flex w-24 items-center justify-center bg-[#f6f9fa] px-4 py-3"
            style={{ borderRight: '1px solid var(--grayscale-100)' }}
          >
            <Typography
              styledAs="bodySmallDMSans"
              weight={600}
              className="text-center"
              {...createTrackingProps({ unmask: true })}
            >
              {section.title}
            </Typography>
          </div>
          <div className="flex flex-1 flex-col">
            {section.rows.map((row, rowIdx) => {
              const isLastRowInSection = rowIdx === section.rows.length - 1;
              return (
                <div
                  key={`${section.title}-${row.label}`}
                  className={`flex bg-white ${isLastRowInSection ? '' : 'border-grayscale-100 border-b-solid'}`}
                >
                  <div className="min-w-[200px] px-4 py-3">
                    <Typography styledAs="bodySmallDMSans" weight={600}>
                      {row.label}
                    </Typography>
                  </div>
                  <div className="flex-1 py-3 pr-4">
                    <Typography styledAs="bodySmallDMSans">{row.value ?? emptyTableCellCopy}</Typography>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const buildCostShareRows = (costShare: CostShare | null): SectionRow[] => [
  {
    label: 'Co-pay',
    value: costShare?.copay ? formatCopay(costShare.copay) : null,
  },
  {
    label: 'Co-insurance',
    value: costShare?.coinsurance ? formatCoinsurance(costShare.coinsurance) : null,
  },
  {
    label: 'Deductible',
    value: costShare?.individual_deductible ? formatAccumulator(costShare.individual_deductible) : null,
  },
  {
    label: 'Out of pocket max',
    value: costShare?.individual_out_of_pocket_max ? formatAccumulator(costShare.individual_out_of_pocket_max) : null,
  },
];

const buildSectionsForVerification = (
  verification: BenefitsVerificationDetails,
  preference: BenefitsPreferenceDetails | null,
): Section[] => {
  const careSiteLabel = preference?.care_site
    ? (SITE_OF_CARE_TYPE_LABELS[preference.care_site as SiteOfCareType] ?? preference.care_site)
    : null;

  return [
    {
      title: 'Coverage',
      rows: [
        { label: 'Coverage status', value: verification.has_mb_coverage ? 'Yes' : 'No' },
        { label: 'Call ID', value: verification.call_reference_id },
        {
          label: 'Number of visits',
          value: verification.number_of_visits != null ? String(verification.number_of_visits) : null,
        },
        { label: 'HCPCS code', value: preference?.hcpcs ?? null },
        { label: 'Admin CPT code', value: preference?.cpt ?? null },
        {
          label: 'Site of care',
          value: preference?.care_site_address ? `${careSiteLabel} — ${preference.care_site_address}` : null,
        },
        {
          label: 'Eligible until',
          value: verification.eligibility_termination_date
            ? formatSimpleDate(verification.eligibility_termination_date)
            : null,
        },
        {
          label: 'Coordination of benefits',
          value: verification.cob_status
            ? verification.cob_status.charAt(0).toUpperCase() + verification.cob_status.slice(1)
            : null,
        },
        {
          label: 'Secondary Payer',
          value: verification.has_other_coverage ? (verification.supplementary_plan_type ?? 'Yes') : null,
        },
        {
          label: 'NPI network status',
          value: verification.administration_network_status
            ? (NETWORK_STATUS_LABELS[verification.administration_network_status] ??
              verification.administration_network_status)
            : null,
        },
      ],
    },
    {
      title: 'Drug cost share',
      rows: buildCostShareRows(verification.drug_cost_share),
    },
    {
      title: 'Admin cost share',
      rows: buildCostShareRows(verification.admin_cost_share),
    },
    ...(verification.notes
      ? [{ title: 'Notes', rows: [{ label: 'Additional notes', value: verification.notes }] }]
      : []),
  ];
};

export type BenefitsVerificationResultsProps = {
  /** Benefits verification response data to display */
  benefitsVerification: BenefitsVerificationResponse;
  /** Additional CSS class name */
  className?: string;
};

/**
 * Displays benefits verification results in a three-column table
 * with section labels spanning rows on the left.
 */
export const BenefitsVerificationResults: React.FC<BenefitsVerificationResultsProps> = ({
  benefitsVerification,
  className,
}) => {
  const { verifications, preference } = benefitsVerification;

  if (verifications.length === 0) return null;

  return (
    <div className={className}>
      {verifications.map((verification, index) => {
        const sections = buildSectionsForVerification(verification, preference);
        return (
          <div key={verification.verification_id}>
            <div className="mb-3 flex items-center justify-between">
              <Typography styledAs="bodyLargeSpaceGrotesk" weight={500} {...createTrackingProps({ unmask: true })}>
                Benefits verification results{verifications.length > 1 ? ` (${index + 1})` : ''}
              </Typography>
              {verification.verified_at && (
                <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                  Received {formatSimpleDate(verification.verified_at)}
                </Typography>
              )}
            </div>

            <BenefitsVerificationTable sections={sections} />

            <Hairline />
          </div>
        );
      })}
    </div>
  );
};

export default BenefitsVerificationResults;
